/*
  rg-jdgm-reviews.js — Grove
  Progressive enhancement for the server-rendered Judge.me "All reviews" block
  (sections/rg-judgeme.liquid). Adds live numbered pagination + click-to-filter
  by star rating, rendering into the same brand card markup as
  snippets/rg-jdgm-review.liquid.

  Also fills the customer-photo strip at the top of the block with EVERY photo
  review Judge.me returns (the public feed = published, non-hidden reviews only),
  loaded lazily when the block nears the viewport so it never costs initial load.

  Data source: Judge.me's public widget JSON endpoint (CORS-open, cacheable):
    https://judge.me/reviews/reviews_for_widget
  It paginates via page/per_page (per_page capped at 30 server-side) but does not
  filter by rating, so star-filtering fetches all pages once, caches them, and
  filters client-side. Honest by design: real reviews only; on any failure the
  server-rendered fallback list + strip stay put.
*/
(function () {
  'use strict';

  var STAR_PATH =
    'M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.79L10 14.77l-5.2 2.74.99-5.79L1.58 7.62l5.82-.85L10 1.5z';
  var API_PER_PAGE = 30; // server cap

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Judge.me HTML-entity-encodes names/titles; decode to real text for the lightbox.
  function decodeEntities(s) {
    var t = document.createElement('textarea');
    t.innerHTML = String(s == null ? '' : s);
    return t.value;
  }

  function textFromHtml(s) {
    var d = document.createElement('div');
    d.innerHTML = String(s == null ? '' : s);
    return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim();
  }

  function stars(rating) {
    var r = Math.round(Number(rating) || 0);
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out +=
        '<svg class="rg-jdgm-star' +
        (i <= r ? ' is-on' : '') +
        '" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="' +
        STAR_PATH +
        '"/></svg>';
    }
    return (
      '<span class="rg-jdgm-stars rg-jdgm-stars--sm" role="img" aria-label="' +
      r +
      ' out of 5 stars">' +
      out +
      '</span>'
    );
  }

  function fmtDate(s) {
    var d = new Date(s);
    if (isNaN(d.getTime())) return '';
    try {
      return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return '';
    }
  }

  function card(r) {
    var html = '<article class="rg-jdgm-review">';
    html += '<header class="rg-jdgm-review__head">';
    html +=
      '<span class="rg-jdgm-review__avatar" aria-hidden="true">' +
      esc(r.reviewer_initial || (r.reviewer_name || '?').charAt(0)) +
      '</span>';
    html += '<div class="rg-jdgm-review__who">';
    // reviewer_name is HTML-entity encoded by Judge.me — render as markup so entities decode.
    html += '<span class="rg-jdgm-review__name">' + (r.reviewer_name || '') + '</span>';
    if (r.verified_buyer) {
      html += '<span class="rg-jdgm-review__verified">✓ Verified</span>';
    }
    html += '</div>';
    var date = fmtDate(r.created_at);
    if (date) html += '<span class="rg-jdgm-review__date">' + date + '</span>';
    html += '</header>';
    html += stars(r.rating);
    if (r.title) html += '<p class="rg-jdgm-review__title">' + r.title + '</p>';
    html += '<div class="rg-jdgm-review__body rte">' + (r.body_html || '') + '</div>';
    var pics = r.pictures_urls || [];
    if (pics.length) {
      html += '<div class="rg-jdgm-review__photos">';
      for (var i = 0; i < pics.length && i < 5; i++) {
        var src = pics[i].compact || pics[i].small || pics[i].original;
        if (!src) continue;
        html +=
          '<img class="rg-jdgm-review__photo" src="' +
          esc(src) +
          '" width="96" height="96" loading="lazy" alt="Customer photo from ' +
          esc(r.reviewer_name) +
          '">';
      }
      html += '</div>';
    }
    html += '</article>';
    return html;
  }

  function Reviews(root) {
    this.root = root;
    this.shop = root.dataset.shop;
    this.pid = root.dataset.productId;
    this.handle = root.dataset.handle || '';
    this.perPage = parseInt(root.dataset.perPage, 10) || 8;
    this.list = root.querySelector('.rg-jdgm__list');
    this.pager = root.querySelector('.rg-jdgm__pager');
    this.histRows = Array.prototype.slice.call(
      root.querySelectorAll('.rg-jdgm-hist__row[data-rating]')
    );
    this.filter = 0; // 0 = all
    this.page = 1;
    this.totalPages = 1;
    this.allCache = null; // all reviews, fetched once for filtering + photo strip
    this.bind();
    this.load(1); // replace the server-rendered fallback with live page 1
    this.initPhotoStrip(); // lazily fill the photo strip with the full feed
  }

  Reviews.prototype.url = function (page, perPage) {
    var p = [
      'url=' + encodeURIComponent(this.shop),
      'shop_domain=' + encodeURIComponent(this.shop),
      'platform=shopify',
      'product_id=' + encodeURIComponent(this.pid),
      'page=' + page,
      'per_page=' + perPage
    ];
    if (this.handle) p.push('product_handle=' + encodeURIComponent(this.handle));
    return 'https://judge.me/reviews/reviews_for_widget?' + p.join('&');
  };

  Reviews.prototype.fetchJSON = function (url) {
    return fetch(url, { credentials: 'omit' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    });
  };

  Reviews.prototype.busy = function (on) {
    this.root.classList.toggle('rg-jdgm--loading', !!on);
  };

  Reviews.prototype.bind = function () {
    var self = this;
    this.histRows.forEach(function (row) {
      row.addEventListener('click', function () {
        var rating = parseInt(row.dataset.rating, 10);
        self.setFilter(self.filter === rating ? 0 : rating);
      });
    });
  };

  Reviews.prototype.setFilter = function (rating) {
    var self = this;
    this.filter = rating;
    this.page = 1;
    this.histRows.forEach(function (row) {
      var on = parseInt(row.dataset.rating, 10) === rating;
      row.classList.toggle('is-active', on);
      row.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (!rating) {
      this.load(1);
      return;
    }
    // Need every review to filter client-side — fetch + cache all pages once.
    if (this.allCache) {
      this.renderFiltered();
      return;
    }
    this.busy(true);
    this.fetchAll()
      .then(function (all) {
        self.allCache = all;
        self.renderFiltered();
      })
      .catch(function () {
        self.busy(false);
        self.filter = 0;
        self.load(1);
      });
  };

  Reviews.prototype.fetchAll = function () {
    var self = this;
    if (this.allCache) return Promise.resolve(this.allCache);
    return this.fetchJSON(this.url(1, API_PER_PAGE)).then(function (first) {
      var total = (first.pagination && first.pagination.total_pages) || 1;
      var all = (first.reviews || []).slice();
      if (total <= 1) return all;
      var jobs = [];
      for (var p = 2; p <= total; p++) {
        jobs.push(
          self.fetchJSON(self.url(p, API_PER_PAGE)).then(function (d) {
            return d.reviews || [];
          })
        );
      }
      return Promise.all(jobs).then(function (pages) {
        pages.forEach(function (rs) {
          all = all.concat(rs);
        });
        return all;
      });
    });
  };

  Reviews.prototype.renderFiltered = function () {
    var subset = this.allCache.filter(
      function (r) {
        return Math.round(Number(r.rating)) === this.filter;
      }.bind(this)
    );
    this.totalPages = Math.max(1, Math.ceil(subset.length / this.perPage));
    var start = (this.page - 1) * this.perPage;
    this.paint(subset.slice(start, start + this.perPage));
    this.busy(false);
  };

  Reviews.prototype.load = function (page) {
    var self = this;
    this.page = page;
    if (this.filter && this.allCache) {
      this.renderFiltered();
      return;
    }
    this.busy(true);
    this.fetchJSON(this.url(page, this.perPage))
      .then(function (d) {
        self.totalPages = (d.pagination && d.pagination.total_pages) || 1;
        self.paint(d.reviews || []);
        self.busy(false);
      })
      .catch(function () {
        self.busy(false); // keep whatever is shown
      });
  };

  Reviews.prototype.paint = function (reviews) {
    if (!reviews.length) {
      this.list.innerHTML =
        '<p class="rg-jdgm__empty">No reviews to show here.</p>';
    } else {
      this.list.innerHTML = reviews.map(card).join('');
    }
    this.renderPager();
    // Keep the reviews in view when paging.
    if (this.page > 1 || this.filter) {
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // --- Customer photo strip: fill with the full Judge.me feed, lazily. ---------

  Reviews.prototype.initPhotoStrip = function () {
    var self = this;
    var section = this.root.closest('.shopify-section') || document;
    this.photoRow = section.querySelector('.rg-jdgm-photos__row');
    if (!this.photoRow) return;
    var strip = section.querySelector('.rg-jdgm-photos');
    var fired = false;
    var trigger = function () {
      if (fired) return;
      fired = true;
      self.loadAllPhotos();
    };
    if ('IntersectionObserver' in window && strip) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              io.disconnect();
              trigger();
            }
          });
        },
        { rootMargin: '600px 0px' }
      );
      io.observe(strip);
    } else {
      trigger();
    }
  };

  Reviews.prototype.loadAllPhotos = function () {
    var self = this;
    this.fetchAll()
      .then(function (all) {
        self.allCache = all;
        self.buildPhotoStrip(all);
      })
      .catch(function () {
        /* keep the server-rendered strip in place */
      });
  };

  Reviews.prototype.buildPhotoStrip = function (reviews) {
    var row = this.photoRow;
    if (!row || !reviews || !reviews.length) return;
    var cells = [];
    for (var i = 0; i < reviews.length; i++) {
      var r = reviews[i];
      var pics = r.pictures_urls || [];
      if (!pics.length) continue;
      var name = decodeEntities(r.reviewer_name || '');
      var titleTxt = decodeEntities(r.title || '').trim();
      var quote = titleTxt || textFromHtml(r.body_html || '');
      var rating = Math.round(Number(r.rating) || 5);
      for (var j = 0; j < pics.length; j++) {
        var big = pics[j].huge || pics[j].original;
        var small = pics[j].small || pics[j].compact || big;
        if (!big) continue;
        cells.push(
          '<li class="rg-jdgm-photos__item">' +
            '<button type="button" class="rg-jdgm-photos__cell" data-rg-jdgm-photo' +
            ' data-full="' + esc(big) + '"' +
            ' data-name="' + esc(name) + '"' +
            ' data-rating="' + rating + '"' +
            ' data-quote="' + esc(quote) + '"' +
            ' aria-label="Open customer photo from ' + esc(name) + '">' +
            '<img src="' + esc(small) + '" width="150" height="150" loading="lazy" decoding="async"' +
            ' alt="Grove desk photographed by ' + esc(name) + '">' +
            '</button>' +
          '</li>'
        );
      }
    }
    if (cells.length) row.innerHTML = cells.join('');
  };

  Reviews.prototype.renderPager = function () {
    if (!this.pager) return;
    var total = this.totalPages;
    if (total <= 1) {
      this.pager.innerHTML = '';
      return;
    }
    var cur = this.page;
    var nums = [];
    var add = function (n) {
      if (nums.indexOf(n) === -1 && n >= 1 && n <= total) nums.push(n);
    };
    add(1);
    add(2);
    for (var d = -1; d <= 1; d++) add(cur + d);
    add(total - 1);
    add(total);
    nums.sort(function (a, b) {
      return a - b;
    });

    var html = '';
    html +=
      '<button class="rg-jdgm-page rg-jdgm-page--nav" data-page="' +
      (cur - 1) +
      '"' +
      (cur === 1 ? ' disabled' : '') +
      ' aria-label="Previous page">‹</button>';
    var prev = 0;
    for (var i = 0; i < nums.length; i++) {
      var n = nums[i];
      if (prev && n - prev > 1) {
        html += '<span class="rg-jdgm-page__gap" aria-hidden="true">…</span>';
      }
      html +=
        '<button class="rg-jdgm-page' +
        (n === cur ? ' is-active' : '') +
        '" data-page="' +
        n +
        '"' +
        (n === cur ? ' aria-current="page"' : '') +
        '>' +
        n +
        '</button>';
      prev = n;
    }
    html +=
      '<button class="rg-jdgm-page rg-jdgm-page--nav" data-page="' +
      (cur + 1) +
      '"' +
      (cur === total ? ' disabled' : '') +
      ' aria-label="Next page">›</button>';
    this.pager.innerHTML = html;

    var self = this;
    Array.prototype.forEach.call(
      this.pager.querySelectorAll('.rg-jdgm-page'),
      function (btn) {
        btn.addEventListener('click', function () {
          var p = parseInt(btn.dataset.page, 10);
          if (p >= 1 && p <= self.totalPages && p !== self.page) self.load(p);
        });
      }
    );
  };

  function init() {
    var nodes = document.querySelectorAll('.rg-jdgm[data-shop][data-product-id]');
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.dataset.rgInit) return;
      node.dataset.rgInit = '1';
      try {
        new Reviews(node);
      } catch (e) {
        /* leave server-rendered fallback in place */
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
